// SetupCompletionPanel — shows %, missing fields per section, and a
// "Mark Setup Complete" button that starts the 1-hour activation timer.
//
// Calls:
//   - GET /api/setup-status (polled every 5s while incomplete)
//   - POST /api/subscriptions/mark-setup-complete (button click)
import { useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CheckCircle2, AlertCircle, Loader2, Settings2, ArrowRight } from "lucide-react";
import { usePolling } from "@/lib/usePolling";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTION_LABELS = {
  schoolIdentity: "School Identity",
  vendors: "Vendor Details",
  cchs: "CCH Details",
};

export default function SetupCompletionPanel({ user, onMarkedComplete }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [marking, setMarking] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/setup-status`);
      setStatus(resp.data);
    } catch (err) {
      console.error("Failed to fetch setup status:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  // iter150-review — polling throttled to 30 s and only runs while
  // the tab is visible + online (via usePolling).
  usePolling(fetchStatus, 30_000);

  const markComplete = async () => {
    setMarking(true);
    try {
      await axios.post(`${API}/subscriptions/mark-setup-complete`);
      toast.success("Setup complete! Your account will be auto-verified in 1 hour.");
      if (onMarkedComplete) await onMarkedComplete();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not mark setup complete");
    } finally {
      setMarking(false);
    }
  };

  if (loading) {
    return (
      <section className="rounded-2xl border-2 border-clay/30 bg-white p-6 shadow-sm" data-testid="setup-completion-loading">
        <div className="flex items-center gap-3">
          <Loader2 className="h-5 w-5 animate-spin text-clay" />
          <p className="text-sm text-ink-700">Checking setup status…</p>
        </div>
      </section>
    );
  }

  if (!status) {
    return null;
  }

  const isComplete = status.isComplete;
  const sections = status.sections || {};
  const missingBySection = status.missingBySection || {};

  return (
    <section className="mb-8" data-testid="setup-completion-panel">
      {/* Heading + percent */}
      <div className="mb-6 rounded-2xl border-2 border-clay/30 bg-gradient-to-br from-clay/5 to-clay/10 p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-[0.7rem] font-semibold uppercase tracking-[0.24em] text-clay">
              Step 2 — Complete your setup
            </p>
            <h2 className="mt-2 font-heading text-2xl font-semibold text-ink-900 sm:text-3xl">
              {isComplete ? "🎉 100% Updated" : `Setup ${status.percent}% Complete`}
            </h2>
            {isComplete ? (
              <p className="mt-2 text-sm text-emerald-700">
                ⏳ Waiting for automatic verification (will activate within 1 hour).
              </p>
            ) : (
              <p className="mt-2 text-sm text-ink-700">
                Please fill in the missing details below to unlock your account.
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <div className="relative h-20 w-20" data-testid="setup-percent-circle">
              <svg className="h-20 w-20 -rotate-90 transform">
                <circle cx="40" cy="40" r="35" stroke="#e5e7eb" strokeWidth="6" fill="none" />
                <circle
                  cx="40"
                  cy="40"
                  r="35"
                  stroke={isComplete ? "#10b981" : "#f59e0b"}
                  strokeWidth="6"
                  fill="none"
                  strokeDasharray={`${(status.percent / 100) * 220} 220`}
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-xl font-bold text-ink-900">{status.percent}%</span>
              </div>
            </div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-4 h-3 w-full overflow-hidden rounded-full bg-gray-200" data-testid="setup-progress-bar">
          <div
            className={`h-full rounded-full transition-all duration-500 ${isComplete ? "bg-emerald-500" : "bg-amber-500"}`}
            style={{ width: `${status.percent}%` }}
          />
        </div>
      </div>

      {/* Section status grid */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 mb-6">
        {Object.entries(SECTION_LABELS).map(([key, label]) => {
          const ok = sections[key];
          const missing = missingBySection[key] || [];
          return (
            <div
              key={key}
              className={`rounded-xl border-2 p-4 ${ok ? "border-emerald-300 bg-emerald-50" : "border-amber-300 bg-amber-50"}`}
              data-testid={`setup-section-${key}`}
            >
              <div className="flex items-center gap-2">
                {ok ? (
                  <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-amber-600" />
                )}
                <h3 className={`text-sm font-semibold ${ok ? "text-emerald-900" : "text-amber-900"}`}>
                  {label}
                </h3>
              </div>
              {ok ? (
                <p className="mt-2 text-xs text-emerald-700">✓ Complete</p>
              ) : (
                <ul className="mt-2 space-y-1 text-xs text-amber-800">
                  {missing.slice(0, 3).map((m, i) => (
                    <li key={i} className="flex gap-1">
                      <span>•</span>
                      <span>{m}</span>
                    </li>
                  ))}
                  {missing.length > 3 && (
                    <li className="font-semibold">…and {missing.length - 3} more</li>
                  )}
                </ul>
              )}
            </div>
          );
        })}
      </div>

      {/* CTA — either "Open Setup" or "Mark Complete" */}
      {!isComplete ? (
        <div className="rounded-xl border border-clay/30 bg-white p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-base font-semibold text-ink-900">Continue filling your setup</h3>
              <p className="mt-1 text-sm text-ink-700">
                Open the Setup page to add/edit School Identity, Vendors & CCHs.
              </p>
            </div>
            <Button asChild className="bg-clay text-white hover:bg-clay-dark" data-testid="open-setup-btn">
              <Link to="/setup">
                <Settings2 className="mr-2 h-4 w-4" /> Open Setup <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      ) : status.setupCompleteAt ? (
        <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50 p-5 text-center" data-testid="setup-marked-complete">
          <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" />
          <h3 className="mt-3 text-lg font-semibold text-emerald-900">
            Setup marked complete — waiting for activation
          </h3>
          <p className="mt-1 text-sm text-emerald-700">
            Your account will be auto-verified within 1 hour. We'll refresh this page automatically.
          </p>
        </div>
      ) : (
        <div className="rounded-xl border-2 border-emerald-300 bg-gradient-to-r from-emerald-50 to-green-50 p-5 text-center">
          <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" />
          <h3 className="mt-3 text-lg font-semibold text-emerald-900">
            All sections complete! Ready to activate your account?
          </h3>
          <p className="mt-1 text-sm text-emerald-700">
            Click below to start the automatic verification timer (1 hour).
          </p>
          <Button
            onClick={markComplete}
            disabled={marking}
            className="mt-4 bg-emerald-600 text-white hover:bg-emerald-700"
            data-testid="mark-setup-complete-btn"
          >
            {marking ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Activating…</>
            ) : (
              <>🚀 Mark Setup Complete & Start Activation</>
            )}
          </Button>
        </div>
      )}
    </section>
  );
}
